import { CSSResultGroup, TemplateResult, html } from 'lit';
import { LightDomStyles, OutlineElement } from '@phase2/outline-core';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './outline-form-baseline.css.lit';
import '@phase2/outline-icon';

@customElement('outline-form-baseline')
export class OutlineFormBaseline extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  lightDomStyles = new LightDomStyles(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  @property({ type: String, attribute: 'form-type', reflect: true })
  formType: 'in-page-search' | null;

  firstUpdated() {
    this.initCustomSelect();
    // this.handleActiveState();
    // Add a global event listener to handle clicks anywhere in the document
    document.addEventListener('click', this.closeAllSelect);
  }

  // Handles the click event on the option element
  handleOptionClick(e: MouseEvent | KeyboardEvent) {
    const target = e.target as HTMLElement;
    // Get the parent select element and previously selected item
    const s: HTMLSelectElement | null | undefined =
      target.parentNode?.parentNode?.querySelector('select');
    const h: HTMLElement | null = target.parentNode
      ?.previousSibling as HTMLElement;

    if (s && h) {
      // Loop over the select element options
      for (let i = 0; i < s.length; i++) {
        // If the clicked option matches the option in the select element
        if (s.options[i].innerHTML == target.innerHTML) {
          // Set the selected index and update the selected item display
          s.selectedIndex = i;
          h.querySelector('span')!.textContent = target.innerHTML;
          // Remove the 'same-as-selected' class from all options
          const y = Array.from(
            target.parentNode?.querySelectorAll('.same-as-selected') || []
          );

          for (let k = 0; k < y.length; k++) {
            y[k].removeAttribute('class');
          }

          // Add 'same-as-selected' class to the clicked option
          target.setAttribute('class', 'same-as-selected');
          break;
        }
      }
      // Trigger the click event on the select box to close it
      h.click();
      this.closeAllSelect();
    }
  }

  // Handles the click event on the selected element
  handleSelectedClick(e: MouseEvent | KeyboardEvent) {
    e.stopPropagation();
    let target = e.target as HTMLElement;
    // If the clicked target is a child of the select box, traverse up to the select box
    if (!target.classList.contains('select-selected')) {
      target = target.closest('.select-selected') as HTMLElement;
    }
    // Toggle aria-expanded and visibility of the select box
    const isExpanded = target.getAttribute('aria-expanded') === 'true';
    target.setAttribute('aria-expanded', String(!isExpanded));
    this.closeAllSelect(e);
    const nextSib = target.nextSibling as HTMLElement;
    nextSib.classList.toggle('select-hide');
    target.classList.toggle('select-arrow-active');
  }

  // Handles the keydown event
  handleKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    switch (e.key) {
      case 'Enter': {
        // If the current focused element is an option, trigger the click event
        if (target.getAttribute('role') === 'option') {
          this.handleOptionClick(e);
        } else {
          // If the select box is focused, open or close it depending on its current state
          this.handleSelectedClick(e);
        }
        break;
      }
      case ' ': {
        // If the Space key is pressed, open or close the select box depending on its current state
        this.handleSelectedClick(e);
        break;
      }
      case 'Escape': {
        // If the Escape key is pressed, close all select boxes
        this.closeAllSelect();
        break;
      }
    }
  }

  // Initializes custom select boxes
  initCustomSelect() {
    // Get all elements with the 'js-form-type-select' class
    const customSelectElements: HTMLElement[] = Array.from(
      this.querySelectorAll('.js-form-type-select')
    );

    customSelectElements.forEach(el => {
      const selElmnt = el.querySelector('select');

      if (selElmnt) {
        // Create a new div to represent the select box
        const a = document.createElement('DIV');
        a.setAttribute('class', 'select-selected');
        a.setAttribute('role', 'button');
        a.setAttribute('aria-haspopup', 'true');
        a.setAttribute('aria-expanded', 'false');
        a.setAttribute('aria-controls', 'select-options');
        a.setAttribute('tabindex', '0'); // Make it focusable
        a.innerHTML = `<span>${
          selElmnt.options[selElmnt.selectedIndex].innerHTML
        }</span> <outline-icon library="material" name="expand_more" size="1.5rem"></outline-icon>`;

        el.appendChild(a);

        // Create a new div for the list of options
        const b = document.createElement('DIV');
        b.setAttribute('id', 'select-options');
        b.setAttribute('role', 'listbox');
        b.setAttribute('class', 'select-items select-hide');

        // Create a new div for each option in the select element
        for (let j = 0; j < selElmnt.length; j++) {
          const c = document.createElement('DIV');
          c.innerHTML = selElmnt.options[j].innerHTML;
          c.addEventListener('click', this.handleOptionClick.bind(this));
          c.addEventListener('keydown', this.handleKeydown.bind(this)); // Add keyboard event
          c.setAttribute('tabindex', '0'); // Make it focusable
          c.setAttribute('role', 'option'); // Give it a role of option
          b.appendChild(c);
        }
        el.appendChild(b);

        // Add event listeners to the select box if the select element is not disabled
        if (!selElmnt.disabled) {
          a.addEventListener('click', this.handleSelectedClick.bind(this));
          a.addEventListener('keydown', this.handleKeydown.bind(this)); // Add keyboard event
        }
      }
    });
  }

  // Close all select boxes
  closeAllSelect(e?: MouseEvent | KeyboardEvent) {
    let elmnt = e?.target as HTMLElement;
    // If the clicked target is a child of the select box, traverse up to the select box
    if (elmnt && !elmnt.classList.contains('select-selected')) {
      elmnt = elmnt.closest('.select-selected') as HTMLElement;
    }
    // Get all select boxes and options containers
    const selectItemsElements: HTMLElement[] = Array.from(
      document.querySelectorAll('.select-items')
    );
    const selectedElements: HTMLElement[] = Array.from(
      document.querySelectorAll('.select-selected')
    );
    const arrNo: number[] = [];

    // Close all select boxes except for the one being clicked on
    selectedElements.forEach((el, index) => {
      if (elmnt == el) {
        arrNo.push(index);
      } else {
        el.classList.remove('select-arrow-active');
        const isExpanded = el.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          el.setAttribute('aria-expanded', 'false');
        }
      }
    });

    selectItemsElements.forEach((el, index) => {
      if (!arrNo.includes(index)) {
        el.classList.add('select-hide');
      }
    });
  }

  // Will trigger the native search form.
  triggerSearch(e: Event) {
    const validNodeNames = ['BUTTON', 'OUTLINE-ICON-BASELINE'];
    const targetNodeName = (e.target as Node).nodeName;

    if (validNodeNames.includes(targetNodeName)) {
      const formElement = this.querySelector(
        '.views-exposed-form'
      ) as HTMLFormElement | null;

      if (formElement) {
        formElement.submit();
      }
    }
  }

  render(): TemplateResult {
    return html`
      ${this.formType === 'in-page-search'
        ? html`
            <div class="in-page-search">
              <slot></slot>
              <div class="icon-wrapper">
                <button class="icon btn primary" @click=${this.triggerSearch}>
                  <outline-icon-baseline
                    name="eu-search"
                    library="system"
                    size="1rem"
                    role="presentation"
                  ></outline-icon-baseline>
                </button>
              </div>
            </div>
          `
        : html` <slot></slot> `}
    `;
  }
}
